import React, { useCallback, useEffect, useId, useState } from "react";
import { FaTimesCircle } from "react-icons/fa";
import { HiOutlineMail } from "react-icons/hi";
import { HiOutlineClipboardDocument } from "react-icons/hi2";

import type { PointsClaimRequest } from "../../lib/api/pointsClaimRequests";
import { formatAmount } from "../../utils/FormatPerson";
import { statusBadgeClass } from "./ordersOrderTable";

type RewardModalProps = {
  setRewardModal: (open: boolean) => void;
  reward: PointsClaimRequest;
};

function displayName(r: PointsClaimRequest): string {
  const p = r.user?.profile;
  const f = p?.firstName?.trim() ?? "";
  const l = p?.lastName?.trim() ?? "";
  const name = `${f} ${l}`.trim();
  if (name) return name;
  const u = p?.username?.trim();
  if (u) return `@${u}`;
  return "Unknown user";
}

function initials(r: PointsClaimRequest): string {
  const p = r.user?.profile;
  const f = p?.firstName?.trim()?.charAt(0) ?? "";
  const l = p?.lastName?.trim()?.charAt(0) ?? "";
  if (f || l) return `${f}${l}`.toUpperCase();
  const u = p?.username?.trim()?.charAt(0);
  if (u) return u.toUpperCase();
  const e = r.user?.email?.address?.trim()?.charAt(0);
  return e ? e.toUpperCase() : "?";
}

function statusPillClass(status: string | undefined): string {
  const u = (status ?? "").toUpperCase();
  const mapped =
    u === "APPROVED"
      ? "COMPLETED"
      : u === "DECLINED"
        ? "FAILED"
        : u === "PENDING"
          ? "PENDING"
          : u;
  return `inline-flex items-center rounded-full border px-2.5 py-0.5 text-[11px] font-bold uppercase tracking-wide ${statusBadgeClass(mapped)}`;
}

function formatClaimWhen(iso: string | undefined): string {
  if (!iso) return "—";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "—";
  const day = date.getDate();
  const ord =
    day % 10 === 1 && day !== 11
      ? "st"
      : day % 10 === 2 && day !== 12
        ? "nd"
        : day % 10 === 3 && day !== 13
          ? "rd"
          : "th";
  return `${date.toLocaleString("default", { month: "short" })} ${day}${ord}, ${date.getFullYear()}, ${date.toLocaleString("default", { hour: "numeric", minute: "numeric", hour12: true })}`;
}

function Row({
  label,
  children,
  mono,
}: {
  label: string;
  children: React.ReactNode;
  mono?: boolean;
}) {
  return (
    <div className="flex flex-col gap-1 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
      <span className="shrink-0 text-[11px] font-semibold uppercase tracking-wider text-[#5C6B76]">
        {label}
      </span>
      <div
        className={`min-w-0 text-right text-[14px] font-medium text-[#E7EBEE] sm:max-w-[65%] ${mono ? "font-mono text-[13px] break-all" : ""}`}
      >
        {children}
      </div>
    </div>
  );
}

function CopyChip({
  value,
  label,
}: {
  value: string;
  label: string;
}) {
  const [copied, setCopied] = useState(false);

  const copy = useCallback(async () => {
    if (!value) return;
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  }, [value]);

  if (!value) return null;

  return (
    <button
      type="button"
      onClick={() => void copy()}
      title={`Copy ${label}`}
      className="ml-2 inline-flex shrink-0 items-center gap-1 rounded-md border border-[#2C363E] bg-[#161E25] px-2 py-1 text-[11px] font-semibold text-[#78C2F7] transition hover:border-[#3A4852] hover:bg-[#1C2227]"
    >
      <HiOutlineClipboardDocument className="text-[14px]" aria-hidden />
      {copied ? "Copied" : "Copy"}
    </button>
  );
}

const RewardModal: React.FC<RewardModalProps> = ({
  setRewardModal,
  reward,
}) => {
  const titleId = useId();
  const email = reward.user?.email?.address?.trim();

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setRewardModal(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [setRewardModal]);

  const bank = reward.bankDetails;
  const hasBank =
    Boolean(bank?.accountNumber?.trim()) ||
    Boolean(bank?.accountName?.trim()) ||
    Boolean(bank?.bankCode?.trim());

  return (
    <div
      className="fixed inset-0 z-[100] flex justify-center items-end sm:items-center p-0 sm:p-6 font-manrope"
      role="presentation"
    >
      <button
        type="button"
        aria-label="Close dialog"
        className="absolute inset-0 bg-black/65 backdrop-blur-sm"
        onClick={() => setRewardModal(false)}
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="relative flex max-h-[min(92vh,720px)] w-full max-w-lg flex-col overflow-hidden rounded-t-2xl border border-[#2C363E] bg-[#13181B] shadow-[0_24px_80px_rgba(0,0,0,0.45)] sm:rounded-2xl"
      >
        <header className="shrink-0 border-b border-[#2C363E] bg-[#161E25]/90 px-5 py-4 sm:px-6">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <h2
                id={titleId}
                className="font-sora text-lg font-semibold tracking-tight text-white"
              >
                Points claim details
              </h2>
              <p className="mt-1 truncate text-[12px] text-[#869AA9] font-mono">
                {reward._id}
              </p>
            </div>
            <button
              type="button"
              onClick={() => setRewardModal(false)}
              className="shrink-0 rounded-lg p-1 text-[#AEBCC6] transition hover:bg-[#273037] hover:text-white"
              aria-label="Close"
            >
              <FaTimesCircle className="text-2xl" />
            </button>
          </div>
        </header>

        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-5 sm:px-6">
          <div className="flex flex-col items-center text-center">
            <div
              className="flex h-[88px] w-[88px] items-center justify-center rounded-full bg-gradient-to-br from-[#0B75C2]/40 to-[#032843] text-[28px] font-bold font-sora text-[#BEE1FB]"
              aria-hidden
            >
              {initials(reward)}
            </div>
            <p className="mt-3 text-[18px] font-semibold font-sora text-white">
              {displayName(reward)}
            </p>
            {reward.user?.profile?.username?.trim() ? (
              <p className="mt-0.5 text-[13px] text-[#869AA9]">
                @{reward.user.profile.username.trim()}
              </p>
            ) : null}
            {email ? (
              <p className="mt-1 max-w-full truncate text-[13px] text-[#AEBCC6]">
                {email}
              </p>
            ) : null}
          </div>

          <div className="mt-6 space-y-4 rounded-xl border border-[#2C363E] bg-[#161E25]/50 p-4">
            <p className="text-[11px] font-bold uppercase tracking-widest text-[#869AA9]">
              Claim
            </p>
            <div className="space-y-3">
              <Row label="Status">
                <span className={statusPillClass(reward.status)}>
                  {reward.status || "—"}
                </span>
              </Row>
              <Row label="Points">
                <span className="tabular-nums text-white">
                  {formatAmount(reward.points)} pts
                </span>
              </Row>
              <Row label="Cash (NGN)">
                <span className="tabular-nums text-[#BEE1FB]">
                  ₦{formatAmount(reward.cashAmount)}
                </span>
              </Row>
              {reward.source ? (
                <Row label="Source">
                  <span className="text-[12px] font-semibold uppercase tracking-wide text-[#BEE1FB]">
                    {String(reward.source).replace(/_/g, " ")}
                  </span>
                </Row>
              ) : null}
              {reward.cashbackId ? (
                <Row label="Cashback ID" mono>
                  <span className="inline-flex flex-wrap items-center justify-end gap-1 sm:justify-end">
                    <span>{reward.cashbackId}</span>
                    <CopyChip value={reward.cashbackId} label="cashback id" />
                  </span>
                </Row>
              ) : null}
              <Row label="Customer ID" mono>
                <span className="inline-flex flex-wrap items-center justify-end gap-1 sm:justify-end">
                  <span>{reward.userId}</span>
                  <CopyChip value={reward.userId} label="customer id" />
                </span>
              </Row>
              <Row label="Submitted">
                {formatClaimWhen(reward.createdAt ?? reward.updatedAt)}
              </Row>
              <Row label="Last updated">
                {formatClaimWhen(reward.updatedAt)}
              </Row>
              {reward.approvedAt ? (
                <Row label="Approved">
                  {formatClaimWhen(reward.approvedAt)}
                </Row>
              ) : null}
              {reward.rejectionReason &&
              String(reward.rejectionReason).trim() &&
              String(reward.rejectionReason).trim().toUpperCase() !== "N/A" ? (
                <div className="rounded-lg border border-amber-500/25 bg-amber-500/[0.06] px-3 py-2.5">
                  <p className="text-[11px] font-bold uppercase tracking-wider text-amber-200/90">
                    Note
                  </p>
                  <p className="mt-1 text-left text-[13px] leading-relaxed text-[#E7EBEE]">
                    {reward.rejectionReason}
                  </p>
                </div>
              ) : null}
            </div>
          </div>

          <div className="mt-4 space-y-3 rounded-xl border border-[#2C363E] bg-[#161E25]/50 p-4">
            <p className="text-[11px] font-bold uppercase tracking-widest text-[#869AA9]">
              Payout account
            </p>
            {hasBank ? (
              <div className="space-y-3">
                <Row label="Account name">
                  {bank?.accountName?.trim() || (
                    <span className="text-[#5C6B76]">Not provided</span>
                  )}
                </Row>
                <Row label="Account number" mono>
                  {bank?.accountNumber?.trim() ? (
                    <span className="inline-flex flex-wrap items-center justify-end gap-1 sm:justify-end">
                      <span>{bank.accountNumber}</span>
                      <CopyChip
                        value={bank.accountNumber}
                        label="account number"
                      />
                    </span>
                  ) : (
                    <span className="text-[#5C6B76]">Not provided</span>
                  )}
                </Row>
                <Row label="Bank code" mono>
                  {bank?.bankCode?.trim() || (
                    <span className="text-[#5C6B76]">—</span>
                  )}
                </Row>
              </div>
            ) : (
              <p className="text-[13px] leading-relaxed text-[#869AA9]">
                No bank details on this claim. The user may still add them for
                pending requests.
              </p>
            )}
          </div>
        </div>

        <footer className="shrink-0 border-t border-[#2C363E] bg-[#0D1114]/80 px-5 py-4 sm:px-6">
          {email ? (
            <a
              href={`mailto:${email}`}
              className="flex h-12 w-full items-center justify-center gap-2 rounded-xl border border-secondary_button/50 bg-secondary_button/15 text-[14px] font-semibold text-[#78C2F7] transition hover:bg-secondary_button/25"
            >
              <HiOutlineMail className="text-xl" aria-hidden />
              Email customer
            </a>
          ) : (
            <p className="text-center text-[12px] text-[#5C6B76]">
              No email on file for this user.
            </p>
          )}
        </footer>
      </div>
    </div>
  );
};

export default RewardModal;
